/**
 * The Quality tab's pure helpers (design "Flow Project Tabs and Quality", boards 4-6; the API contract "Flow Quality
 * tab v1"): the checks and their groups, the status and score words and tones, relative times and durations, the trend
 * sparkline, the coverage tone, the three lines of a card, the "Fix with AI" prompts, the gate rules of the settings and
 * the ignore-rule form's checks.
 */

export const CHECK_IDS = ['tests', 'lint', 'secrets', 'security', 'vulnerabilities', 'dependencies', 'sbom', 'readiness', 'codeStats', 'privacy', 'apiContract', 'accessibility', 'performance', 'codeScanning', 'external', 'lighthouse'];
// Run by the web app itself (axe-core on the preview), not by the sandbox's engine.
export const BROWSER_CHECKS = ['accessibility'];

/**
 * Whether this browser runs a check itself: the overview's `browserChecks` says so (empty once the Flow has the headless
 * browser job); an older sandbox without the list: as before, unless it says the headless job is available.
 */
export function runsInBrowser(overview, id = 'accessibility') {
  if (Array.isArray(overview?.browserChecks)) { return overview.browserChecks.includes(id); }
  if (overview?.headless?.available) { return false; }
  return BROWSER_CHECKS.includes(id);
}

// The icons are 16x16 paths (stroke), as the design draws them.
export const CHECKS = {
  tests: { title: 'Tests', icon: 'M6 2h4M7 2v4L3.5 12.5A1 1 0 0 0 4.4 14h7.2a1 1 0 0 0 .9-1.5L9 6V2' },
  lint: { title: 'Lint and formatting', icon: 'M2 4h12M2 8h8M2 12h5M10 12l1.5 1.5L14 11' },
  secrets: { title: 'Secrets in code', icon: 'M5 7V5a3 3 0 0 1 6 0v2M3.5 7h9v7h-9zM8 10v1.5' },
  security: { title: 'Security issues', icon: 'M8 1.8l5 2v4c0 3-2.2 5.3-5 6.4C5.2 13.1 3 10.8 3 7.8v-4zM8 6v3M8 11v.5' },
  vulnerabilities: { title: 'Vulnerabilities', icon: 'M8 2l6.5 11.5h-13zM8 6.5v3M8 11.5v.5' },
  dependencies: { title: 'Dependencies and licences', icon: 'M8 2l5.5 3v6L8 14l-5.5-3V5zM8 8l5.5-3M8 8v6M8 8L2.5 5' },
  sbom: { title: 'SBOM', icon: 'M4 2h6l3 3v9H4zM10 2v3h3M6 8h5M6 11h5' },
  readiness: { title: 'Ready for production', icon: 'M2.5 8.5l3.5 3.5 7.5-8' },
  codeStats: { title: 'Code stats', icon: 'M3 13V8M8 13V3M13 13V6' },
  performance: { title: 'Performance', icon: 'M8 14A6 6 0 1 1 14 8M8 8l3-3' },
  accessibility: { title: 'Accessibility', icon: 'M8 2.5v.5M3 6l5 1 5-1M8 7v3l-2 4M8 10l2 4' },
  apiContract: { title: 'API contract', icon: 'M5 4L2 8l3 4M11 4l3 4-3 4' },
  privacy: { title: 'Privacy', icon: 'M4 7V5a4 4 0 0 1 8 0v2M3 7h10v7H3z' },
  codeScanning: { title: 'Code scanning', icon: 'M7 2.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9zM10.3 10.3L14 14M5 7h4' },
  external: { title: 'External tools', icon: 'M9 2h5v5M14 2L7.5 8.5M12 9.5V14H2V4h4.5' },
  lighthouse: { title: 'Lighthouse', icon: 'M6.5 5h3l1 9h-5zM6 2.5h4V5H6zM4.5 14h7M3 3.5l1.5.7M13 3.5l-1.5.7' },
};

// "Can I prove it?" also holds two areas that are not checks (the engine never runs them; the server computes them when
// read): the signed build provenance of each release and the compliance mapping. Their cards and details pages.
export const PROVE_AREAS = {
  provenance: { title: 'Build provenance', icon: 'M8 2a3 3 0 0 1 3 3v1H5V5a3 3 0 0 1 3-3zM4 6h8v8H4zM8 9v2', purpose: 'Each release records which commits and files it shipped, signed, so anyone can check that what runs is what you reviewed.' },
  compliance: { title: 'Compliance mapping', icon: 'M3 3h10v10H3zM5.5 8l2 2 3-4', purpose: 'Maps these checks to the controls of OWASP ASVS and ISO 27001, and shows which ones already have evidence.' },
};

// What each area is for, in a line (the card's title tooltip and the details page).
export const CHECK_PURPOSE = {
  tests: 'Runs each service\'s own tests and measures how much of the code they cover.',
  lint: 'Looks for likely bugs and messy code with each service\'s linter (or Flow\'s built-in rules).',
  readiness: 'Finds what works in the preview but would break once the app is published.',
  codeStats: 'Counts the code per language and service, and spots very complex, very long or copied code.',
  performance: 'Measures how heavy the web pages are and how fast the server answers.',
  accessibility: 'Checks the running pages for problems that stop people using a keyboard or a screen reader.',
  apiContract: 'Compares each service\'s API with the last published one, so nothing that others call breaks.',
  vulnerabilities: 'Looks up every package in the public vulnerability database (OSV).',
  security: 'Looks for risky code and container or Kubernetes settings.',
  secrets: 'Looks for passwords, keys and tokens written in the code.',
  privacy: 'Finds personal data (emails, phone numbers, tax codes…) in the code, the logs and the databases. Names only, never values.',
  dependencies: 'Lists the packages each service uses and checks their licences.',
  sbom: 'The list of every component in the project, in the standard formats auditors ask for.',
  codeScanning: 'Scans the code of languages Flow\'s own rules do not cover (C#, Rust, PHP, Ruby) with Semgrep, and the Dockerfiles with Hadolint.',
  external: 'Brings in what your organization\'s tools found: SonarQube or SonarCloud, the Semgrep AppSec Platform, and SARIF files from any other scanner.',
  lighthouse: 'Scores each running web page for performance, accessibility, best practices and SEO, in a headless browser.',
};

export const GROUPS = [
  { id: 'works', name: 'Does it work?', desc: 'Tests, readiness for production, speed and access for everyone', checks: ['tests', 'readiness', 'lint', 'codeStats', 'performance', 'accessibility', 'apiContract', 'lighthouse'] },
  { id: 'safe', name: 'Is it safe?', desc: 'Known vulnerabilities, risky code, secrets and personal data', checks: ['vulnerabilities', 'security', 'secrets', 'privacy', 'codeScanning', 'external'] },
  { id: 'prove', name: 'Can I prove it?', desc: 'What auditors, customers and your IT team will ask for', checks: ['dependencies', 'sbom'] },
];

export const checkTitle = (id) => CHECKS[id]?.title || PROVE_AREAS[id]?.title || id;

/**
 * The Quality header's section menu: the overview's sections (stable element ids, scrolled to) and Settings (a view of
 * its own, opened instead).
 */
export const SECTIONS = [
  { key: 'overview', label: 'Overview', id: 'quality-section-overview' },
  { key: 'gate', label: 'Release gate', id: 'quality-section-gate' },
  { key: 'works', label: 'Does it work?', id: 'quality-section-works' },
  { key: 'safe', label: 'Is it safe?', id: 'quality-section-safe' },
  { key: 'prove', label: 'Can I prove it?', id: 'quality-section-prove' },
  { key: 'settings', label: 'Settings', id: 'quality-settings', view: 'settings' },
];
export const sectionId = (key) => SECTIONS.find((s) => s.key === key)?.id;

// v3 build provenance.
export const shortSha = (sha) => (sha ? String(sha).slice(0, 7) : '');
export const shortDigest = (digest) => (digest ? String(digest).replace(/^sha256:/, '').slice(0, 12) : '');

/** A release's gate in words and tone: passed, overridden by X, blocked (or not checked). */
export function provenanceGate(summary) {
  const gate = summary?.gate || {};
  if (gate.overridden || summary?.override) {
    const by = summary?.override?.by;
    const who = (by && typeof by === 'object' ? by.name || by.email : by) || 'an owner';
    return { tone: 'warn', label: `Overridden by ${who}` };
  }
  if (gate.blocked) { return { tone: 'fail', label: 'Blocked' }; }
  if (gate.neverRan) { return { tone: 'info', label: 'Not checked' }; }
  return { tone: 'pass', label: gate.stale ? 'Passed (files changed since)' : 'Passed' };
}

const DESTINATIONS = { console: 'Console', git: 'Git', zip: 'Release zip' };
/** "Console · shop (dev)", "Git · me/shop", "Release zip". */
export function destinationText(destination) {
  if (!destination) { return '–'; }
  const kind = DESTINATIONS[destination.kind] || destination.kind || 'Unknown';
  const target = destination.target ? ` · ${destination.target}` : '';
  return `${kind}${target}${destination.environment ? ` (${destination.environment})` : ''}`;
}

export const KEY_SOURCES = {
  explicit: 'An explicit key set by your Flow administrator',
  signing_secret: 'Derived from the Flow installation\'s signing secret',
  app_secrets_key: 'Derived from the Flow installation\'s app secrets key',
  dev_file: 'A local development key (a file on this machine): fine for trying Flow out, not for real releases',
};
export const keySourceText = (source) => KEY_SOURCES[source] || source || 'Unknown';

// v3 compliance mapping. The wording is "evidence for" / "helps with", never "compliant" or "certified".
export const CONTROL_STATUS = {
  evidenced: { label: 'Evidenced', tone: 'pass' },
  failing: { label: 'Failing', tone: 'fail' },
  no_evidence: { label: 'No evidence yet', tone: 'info' },
  not_covered: { label: 'Not covered by Flow', tone: 'info' },
};
export const controlStatusOf = (status) => CONTROL_STATUS[status] || { label: status || 'Unknown', tone: 'info' };
export const SOURCE_STATE = { pass: { label: 'Passes', tone: 'pass' }, fail: { label: 'Fails', tone: 'fail' }, none: { label: 'Nothing yet', tone: 'info' } };

const STATUS = {
  pass: { label: 'Passing', tone: 'pass' },
  warn: { label: 'Attention', tone: 'warn' },
  fail: { label: 'Failing', tone: 'fail' },
  info: { label: 'Info', tone: 'info' },
  skipped: { label: 'Not run', tone: 'info' },
  // Nothing for the check to look at, and that is fine (code scanning: Flow's built-in rules cover the project).
  not_needed: { label: 'Not needed', tone: 'info' },
  unsupported: { label: 'Not supported', tone: 'info' },
  cant_run: { label: 'Can\'t run', tone: 'warn' },
  error: { label: 'Could not run', tone: 'warn' },
  not_ready: { label: 'Not ready', tone: 'info' },
  offline: { label: 'Offline', tone: 'info' },
  running: { label: 'Running', tone: 'prog' },
  queued: { label: 'Queued', tone: 'prog' },
};

/** `{ label, tone }` of a check status; tone is one of pass, warn, fail, info, prog (the design's pill classes). */
export const statusOf = (status) => STATUS[status] || { label: status ? String(status) : 'Unknown', tone: 'info' };

const SEVERITY = {
  critical: { label: 'Critical', tone: 'fail' },
  high: { label: 'High', tone: 'fail' },
  medium: { label: 'Medium', tone: 'warn' },
  low: { label: 'Low', tone: 'info' },
  info: { label: 'Info', tone: 'info' },
};
export const SEVERITIES = ['critical', 'high', 'medium', 'low', 'info'];
export const severityOf = (severity) => SEVERITY[severity] || { label: severity || '–', tone: 'info' };

export const plural = (n, word, many = `${word}s`) => `${n} ${n === 1 ? word : many}`;

/** The score's tone (the ring's colour): pass from 85, warn from 60, else fail; none before a run. */
export function scoreTone(score) {
  if (score === null || score === undefined) { return 'info'; }
  if (score >= 85) { return 'pass'; }
  if (score >= 60) { return 'warn'; }
  return 'fail';
}

/** The headline next to the ring. */
export function scoreTitle(score, gate) {
  if (score === null || score === undefined) { return 'Not checked yet'; }
  if (gate?.blocked) { return 'Publish is blocked'; }
  if (score >= 85) { return 'Looking good'; }
  if (score >= 60) { return 'Needs attention'; }
  return 'Needs work';
}

/** The ring: the arc's dash for a circle of radius `r` (`"166.6 213.6"`). */
export function ringDash(score, r = 34) {
  const total = 2 * Math.PI * r;
  const share = Math.max(0, Math.min(100, Number(score) || 0)) / 100;
  return `${(share * total).toFixed(1)} ${total.toFixed(1)}`;
}

/** A card's tone now: in progress while its check runs or waits, else its status's tone. */
export function cardTone(id, card, running = null) {
  const live = running?.checks?.[id];
  if (live === 'running' || live === 'queued') { return 'prog'; }
  return statusOf(shownStatus(card)).tone;
}

/** The status line: `[{ tone, text }]` "2 failing", "1 needs attention", "4 passing", "1 in progress", "2 info only". */
export function statusCounts(checks = {}, running = null) {
  const count = { fail: 0, warn: 0, pass: 0, prog: 0, info: 0 };
  CHECK_IDS.forEach((id) => {
    const card = checks[id];
    if (!card) { return; }
    count[cardTone(id, card, running)] += 1;
  });
  const out = [];
  if (count.fail) { out.push({ tone: 'fail', text: `${count.fail} failing` }); }
  if (count.warn) { out.push({ tone: 'warn', text: `${count.warn} ${count.warn === 1 ? 'needs' : 'need'} attention` }); }
  if (count.pass) { out.push({ tone: 'pass', text: `${count.pass} passing` }); }
  if (count.prog) { out.push({ tone: 'prog', text: `${count.prog} in progress` }); }
  if (count.info) { out.push({ tone: 'info', text: `${count.info} info only` }); }
  return out;
}

/** "just now", "4 min ago", "2 h ago", "yesterday", "3 days ago", else the date. */
export function relativeTime(ms, now = Date.now()) {
  if (!ms) { return ''; }
  const diff = Math.max(0, now - ms);
  const min = Math.floor(diff / 60000);
  if (min < 1) { return 'just now'; }
  if (min < 60) { return `${min} min ago`; }
  const hours = Math.floor(min / 60);
  if (hours < 24) { return `${hours} h ago`; }
  const days = Math.floor(hours / 24);
  if (days === 1) { return 'yesterday'; }
  if (days < 7) { return `${days} days ago`; }
  return new Date(ms).toLocaleDateString(undefined, { day: 'numeric', month: 'short', year: 'numeric' });
}

/** "350 ms", "12 s", "1 m 48 s", "1 h 2 m". */
export function formatDuration(ms) {
  if (ms === null || ms === undefined || Number.isNaN(Number(ms))) { return '–'; }
  if (ms < 1000) { return `${Math.round(ms)} ms`; }
  const s = Math.round(ms / 1000);
  if (s < 60) { return `${s} s`; }
  const m = Math.floor(s / 60);
  if (m < 60) { return `${m} m ${s % 60} s`; }
  return `${Math.floor(m / 60)} h ${m % 60} m`;
}

/** Counts in short: 1234 → "1.2k". */
export function formatCount(n) {
  if (n === null || n === undefined) { return '–'; }
  if (n >= 10000) { return `${Math.round(n / 1000)}k`; }
  if (n >= 1000) { return `${(n / 1000).toFixed(1)}k`; }
  return String(n);
}

const LEVEL = { pass: 1, info: 0.75, warn: 0.5, fail: 0 };

/**
 * The trend of a check over the last runs (`history`, oldest first), as SVG polyline points in a `width` x `height`
 * box: pass high, warn in the middle, fail low; runs where the check did not give a verdict are left out. Fewer than
 * 2 points: ''. Without `checkId`: the score.
 */
export function sparkPoints(history = [], checkId = null, { width = 96, height = 30, pad = 4 } = {}) {
  const values = history.map((h) => {
    if (!checkId) { return h.score === null || h.score === undefined ? null : h.score / 100; }
    const level = LEVEL[h.checks?.[checkId]];
    return level === undefined ? null : level;
  }).filter((v) => v !== null);
  if (values.length < 2) { return ''; }
  const step = width / (values.length - 1);
  return values.map((v, i) => `${Math.round(i * step * 10) / 10},${Math.round((pad + (1 - v) * (height - 2 * pad)) * 10) / 10}`).join(' ');
}

/** The trend in words, for the sparkline's label. */
export function trendLabel(history = [], checkId) {
  const statuses = history.map((h) => h.checks?.[checkId]).filter((s) => LEVEL[s] !== undefined);
  if (statuses.length < 2) { return 'No trend yet'; }
  const fails = statuses.filter((s) => s === 'fail').length;
  return `Last ${statuses.length} runs: ${statuses.length - fails} without a failure, the latest ${statusOf(statuses[statuses.length - 1]).label.toLowerCase()}`;
}

/** The coverage tone against the rule's minimum: pass at or over it, warn up to 20 points under, else fail. */
export function coverageTone(pct, min = 70) {
  if (pct === null || pct === undefined) { return 'info'; }
  if (pct >= min) { return 'pass'; }
  if (pct >= min - 20) { return 'warn'; }
  return 'fail';
}

export const pct = (value) => (value === null || value === undefined ? '–' : `${Math.round(value)}%`);

export const fileLine = (finding) => (finding?.file ? `${finding.file}${finding.line ? `:${finding.line}` : ''}` : '');

/** One finding in a line: what the overview's card and the prompts say. */
export function findingLine(checkId, finding) {
  const extra = finding.extra || {};
  if (checkId === 'vulnerabilities') {
    return [extra.package && `${extra.package} ${extra.version || ''}`.trim(), finding.rule, finding.fix].filter(Boolean).join(' · ');
  }
  if (checkId === 'tests') {
    const name = extra.name || finding.message;
    return finding.rule === 'test-flaky' ? `Flaky: ${name}` : `${name}${finding.service ? ` (${finding.service})` : ''}`;
  }
  if (checkId === 'dependencies' && extra.package) {
    return `${extra.package}${extra.version ? ` ${extra.version}` : ''}: ${finding.message}`;
  }
  const where = fileLine(finding);
  return `${finding.message}${where ? ` · ${where}` : ''}`;
}

/** The card's three lines: its worst findings, else what the details tell, else its message. */
export function cardLines(id, card) {
  if (!card) { return []; }
  const lines = (card.top || []).slice(0, 3).map((f) => findingLine(id, f));
  const d = card.details || {};
  if (lines.length < 3 && id === 'tests' && d.services) {
    const measured = d.services.filter((s) => s.coverage && s.coverage.lines !== null && s.coverage.lines !== undefined);
    if (measured.length) { lines.push(`Coverage: ${measured.map((s) => `${s.service} ${pct(s.coverage.lines)}`).join(', ')}`); }
    if (d.totals?.flaky) { lines.push(plural(d.totals.flaky, 'flaky test')); }
  }
  if (lines.length < 3 && id === 'dependencies') {
    const licences = d.licences || [];
    if (licences.length) {
      const allowed = licences.filter((l) => l.allowed).reduce((s, l) => s + (l.count || 0), 0);
      const review = licences.filter((l) => !l.allowed).reduce((s, l) => s + (l.count || 0), 0);
      lines.push(`Licences: ${allowed} allowed${review ? `, ${review} to review` : ''}`);
    }
    if (d.lockfiles?.length) { lines.push(`Lockfiles: ${d.lockfiles.map((l) => l.service).filter(Boolean).join(', ')}`); }
    if (d.outdated === null && d.outdatedNote) { lines.push(d.outdatedNote); }
  }
  if (lines.length < 3 && id === 'sbom' && d.components) {
    lines.push(`${plural(d.components, 'component')} · CycloneDX ${d.specVersion || '1.5'} and SPDX`);
  }
  if (id === 'codeStats') {
    const langs = (d.languages || []).filter((l) => !l.other).slice(0, 3);
    if (langs.length) { lines.push(langs.map((l) => `${l.language} ${l.share ?? 0}%`).join(' · ')); }
    if (d.complexity?.functions) { lines.push(`Average complexity ${d.complexity.average} · ${plural(d.complexity.over || 0, 'function')} over ${d.complexity.threshold || 15}`); }
    if (d.duplication && typeof d.duplication.percent === 'number') { lines.push(`Duplication ${d.duplication.percent}%${d.todos?.total ? ` · ${plural(d.todos.total, 'TODO')}` : ''}`); }
  }
  if (lines.length < 3 && id === 'apiContract' && d.endpoints !== undefined) {
    const added = (d.services || []).reduce((n, x) => n + (x.added || 0), 0);
    const noAuth = (d.services || []).reduce((n, x) => n + (x.noAuth || 0), 0);
    lines.push(`${plural(d.endpoints || 0, 'endpoint')}${added ? `, ${added} new` : ''}`);
    if (noAuth) { lines.push(`${plural(noAuth, 'endpoint')} without login: check they should be public`); }
    if (!d.snapshot) { lines.push('No published contract yet: the first Publish saves one'); }
  }
  if (lines.length < 3 && id === 'performance') {
    (d.bundles || []).filter((b) => b.jsGzipBytes).slice(0, 2).forEach((b) => lines.push(`${b.service}: ${Math.round(b.jsGzipBytes / 1024)} KB of JavaScript (gzip)`));
    const slow = (d.routes || [])[0];
    if (slow && lines.length < 3) { lines.push(`Slowest: ${slow.route} (${slow.service}) p95 ${slow.p95} ms`); }
    if (d.tracesNote && lines.length < 3) { lines.push(d.tracesNote); }
  }
  if (lines.length < 3 && id === 'accessibility' && card.counts?.pages) {
    lines.push(`${card.counts.critical || 0} critical, ${card.counts.serious || 0} serious, ${card.counts.moderate || 0} moderate`);
  }
  if (lines.length < 3 && ['codeScanning', 'external', 'lighthouse'].includes(id)) { lines.push(...toolCardLines(id, d)); }
  if (card.ignored) { lines.push(`${card.ignored} ignored by a rule`); }
  if (!lines.length && card.message) { lines.push(card.message); }
  if (!lines.length && card.summary) { lines.push(card.summary); }
  return lines.slice(0, 3);
}

/** The card lines of the three tool checks: the tools that ran, the sources, the Lighthouse scores per page. */
function toolCardLines(id, d) {
  if (id === 'codeScanning') {
    const ran = (d.tools || []).filter((t) => t.state === 'done').map((t) => t.name);
    return [ran.length ? `Scanned with ${ran.join(' and ')}${(d.languages || []).length ? ` · ${d.languages.map(languageName).join(', ')}` : ''}` : null].filter(Boolean);
  }
  if (id === 'external') {
    return (d.sources || []).filter((s) => s.state !== 'off').slice(0, 2).map((s) => `${s.name}: ${s.state === 'error' ? 'could not run' : s.gate ? `quality gate ${GATE_WORDS[s.gate.status] || s.gate.status}` : plural(s.findings || 0, 'finding')}`);
  }
  return (d.pages || []).filter((p) => p.scores).slice(0, 2).map((p) => `${p.service}: ${LIGHTHOUSE_CATEGORIES.map((c) => `${c.short} ${p.scores[c.key] ?? '–'}`).join(' · ')}`);
}

// ---- code scanning, external tools, Lighthouse --------------------------------------------------------------------

const LANGUAGES = { csharp: 'C#', rust: 'Rust', php: 'PHP', ruby: 'Ruby', dockerfile: 'Dockerfile' };
export const languageName = (id) => LANGUAGES[id] || id;

export const GATE_WORDS = { OK: 'passed', WARN: 'warns', ERROR: 'failed', NONE: 'not set' };
/** A Sonar quality gate's status as a pill. */
export const gateStatusOf = (status) => ({
  OK: { label: 'Passed', tone: 'pass' }, WARN: { label: 'Warns', tone: 'warn' }, ERROR: { label: 'Failed', tone: 'fail' }, NONE: { label: 'No gate', tone: 'info' },
}[status] || { label: status || 'Unknown', tone: 'info' });

export const SOURCE_KINDS = { sonar: 'SonarQube', semgrep: 'Semgrep AppSec Platform', sarif: 'SARIF import' };
export const TOOL_STATE = {
  done: { label: 'Ran', tone: 'pass' }, missing: { label: 'Not installed', tone: 'warn' }, error: { label: 'Could not run', tone: 'warn' }, skipped: { label: 'Nothing to scan', tone: 'info' }, not_needed: { label: 'Not needed', tone: 'info' }, off: { label: 'Off', tone: 'info' },
};
export const toolStateOf = (state) => TOOL_STATE[state] || { label: state || 'Unknown', tone: 'info' };

/** Where an imported SARIF file came from (the import form's choices, in order). */
export const SARIF_SOURCES = [
  { id: 'snyk', label: 'Snyk' },
  { id: 'github', label: 'GitHub code scanning' },
  { id: 'gitlab', label: 'GitLab code scanning' },
  { id: 'defectdojo', label: 'DefectDojo' },
  { id: 'sonar', label: 'SonarQube' },
  { id: 'semgrep', label: 'Semgrep' },
  { id: 'other', label: 'Other' },
];
export const sarifSourceName = (id) => SARIF_SOURCES.find((s) => s.id === id)?.label || id || 'Other';
export const SARIF_MAX_BYTES = 10 * 1024 * 1024;

export const LIGHTHOUSE_CATEGORIES = [
  { key: 'performance', label: 'Performance', short: 'Perf' },
  { key: 'accessibility', label: 'Accessibility', short: 'A11y' },
  { key: 'bestPractices', label: 'Best practices', short: 'BP' },
  { key: 'seo', label: 'SEO', short: 'SEO' },
];

/** A Lighthouse score's tone, as Lighthouse itself colours it: 90 and over good, 50-89 to improve, under 50 poor. */
export function lighthouseTone(score) {
  if (score === null || score === undefined) { return 'info'; }
  if (score >= 90) { return 'pass'; }
  if (score >= 50) { return 'warn'; }
  return 'fail';
}

/** The key metrics of a Lighthouse page: LCP, TBT, CLS, FCP, in words ("2.4 s", "180 ms", "0.05"). */
export function lighthouseMetrics(metrics = {}) {
  const seconds = (ms) => (ms === null || ms === undefined ? '–' : ms >= 1000 ? `${(ms / 1000).toFixed(1)} s` : `${Math.round(ms)} ms`);
  return [
    { key: 'lcp', label: 'Largest Contentful Paint', short: 'LCP', value: seconds(metrics.lcpMs) },
    { key: 'tbt', label: 'Total Blocking Time', short: 'TBT', value: seconds(metrics.tbtMs) },
    { key: 'cls', label: 'Cumulative Layout Shift', short: 'CLS', value: metrics.cls === null || metrics.cls === undefined ? '–' : String(Math.round(metrics.cls * 1000) / 1000) },
    { key: 'fcp', label: 'First Contentful Paint', short: 'FCP', value: seconds(metrics.fcpMs) },
  ];
}

/** When a check runs by itself, from `settings.when[id]`. */
export function whenText(when) {
  if (!when) { return 'manual runs'; }
  if (when.change && when.publish) { return 'on every change and before Publish'; }
  if (when.change) { return 'on every change'; }
  if (when.publish) { return 'before Publish'; }
  return 'manual runs';
}

// The checks whose findings the AI can fix from a prompt.
export const FIXABLE = ['vulnerabilities', 'security', 'secrets', 'tests', 'lint', 'readiness', 'privacy', 'accessibility', 'performance', 'codeScanning', 'external', 'lighthouse'];

/** The card's action: "Fix with AI" for a failing fixable check, "Download" for the SBOM, else "Open". */
export function cardAction(id, card) {
  if (id === 'sbom' && card?.status === 'pass') { return 'download'; }
  if (FIXABLE.includes(id) && card && ['fail', 'warn'].includes(card.status) && (card.top || []).length) { return 'fix'; }
  return 'open';
}

// ---- prompts for "Fix with AI" --------------------------------------------------------------------------------------

/** A failing test: its name, where, the error; fix the code (or the test, if it is the one that is wrong) and rerun. */
export function testPrompt(finding) {
  const extra = finding.extra || {};
  const lines = [`A test of this project fails. Find out why and fix it, then run the tests of ${finding.service ? `the ${finding.service} service` : 'the project'} again to check.`, ''];
  lines.push(`Test: ${[extra.suite, extra.name].filter(Boolean).join(' › ') || finding.message}`);
  if (finding.file) { lines.push(`File: ${fileLine(finding)}`); }
  if (finding.message && finding.message !== extra.name) { lines.push(`Error: ${finding.message}`); }
  if (extra.expected !== undefined) { lines.push(`Expected: ${extra.expected}`); }
  if (extra.received !== undefined) { lines.push(`Received: ${extra.received}`); }
  if (extra.stack) { lines.push('', 'Stack:', String(extra.stack).split('\n').slice(0, 12).join('\n')); }
  lines.push('', 'Fix the code if the test is right; change the test only if it checks the wrong thing, and say which.');
  return lines.join('\n');
}

/** A vulnerability: the package, version, advisory, fixed version; upgrade it (or replace it) and rerun. */
export function vulnerabilityPrompt(finding) {
  const extra = finding.extra || {};
  const ids = [finding.rule, ...(extra.ids || [])].filter((v, i, list) => v && list.indexOf(v) === i);
  const lines = ['A dependency of this project has a known vulnerability. Fix it, then run the Quality checks again to confirm.', ''];
  lines.push(`Package: ${extra.package || '?'} ${extra.version || ''}`.trim() + (extra.ecosystem ? ` (${extra.ecosystem})` : ''));
  lines.push(`Advisory: ${ids.join(', ')} · ${severityOf(finding.severity).label}`);
  if (finding.message) { lines.push(`What: ${finding.message}`); }
  if (extra.lockfile || finding.service) { lines.push(`Where: ${[finding.service, extra.lockfile].filter(Boolean).join(' · ')}`); }
  if (extra.fixed) {
    lines.push(`Fixed in: ${extra.fixed}.`, '', ...vulnerabilitySteps(finding.service, extra));
  } else {
    lines.push('There is no fixed version yet: replace the package with a maintained alternative, then refresh the lockfile and run the Vulnerabilities check again.');
  }
  if (extra.url) { lines.push(`Details: ${extra.url}`); }
  return lines.join('\n');
}

/**
 * The steps of a vulnerability with a fixed version: bump it in the manifest, refresh the lockfile through Flow (the
 * vfs_quality_lockfiles tool runs npm where the registry is reachable; the AI's own shell has no npm), check again.
 */
function vulnerabilitySteps(service, extra) {
  const where = service ? `services/${service}/` : 'the service\'s ';
  if (extra.ecosystem && String(extra.ecosystem).toLowerCase() !== 'npm') {
    return [
      `1. Pin ${extra.package} to ${extra.fixed} or later in ${where}requirements.txt (or pyproject.toml).`,
      '2. Run the Vulnerabilities check again (vfs_quality_run with checks ["vulnerabilities"]) and confirm the advisory is gone.',
    ];
  }
  return [
    `1. Raise ${extra.package} to ${extra.fixed} or later in ${where}package.json (a dependency of a dependency: add an "overrides" entry).`,
    `2. Refresh the lockfile from package.json with vfs_quality_lockfiles (services ${service ? `["${service}"]` : 'of that service'}, refresh true): it runs npm where the registry is reachable. Do not edit the lockfile by hand.`,
    '3. Run the Vulnerabilities check again (vfs_quality_run with checks ["vulnerabilities"]) and confirm the advisory is gone.',
  ];
}

/** A list of findings of one check (security rules, secrets, lint...): fix them all and rerun the check. */
export function findingsPrompt(checkId, findings = []) {
  const title = checkTitle(checkId).toLowerCase();
  const lines = [`The Quality check "${checkTitle(checkId)}" found ${plural(findings.length, 'problem')} in this project. Fix them, then run the check again.`, ''];
  findings.slice(0, 20).forEach((f) => {
    const where = fileLine(f);
    lines.push(`- [${severityOf(f.severity).label}] ${f.rule}: ${f.message}${where ? ` (${where})` : ''}${f.fix ? `. Fix: ${f.fix}` : ''}`);
  });
  if (findings.length > 20) { lines.push(`- …and ${findings.length - 20} more of the same check.`); }
  if (checkId === 'secrets') {
    lines.push('', 'Move each secret out of the code (an environment variable or the project secrets), and tell me which keys to rotate since they were committed.');
  } else if (title) {
    lines.push('', 'Keep the behaviour of the app the same.');
  }
  return lines.join('\n');
}

/** The prompt for a card's "Fix with AI": its worst findings. */
export function cardPrompt(id, card) {
  const top = card?.top || [];
  if (id === 'readiness') { return readinessPrompt(top.map(readinessItemOf)); }
  if (id === 'accessibility') { return accessibilityPrompt(top); }
  if (id === 'tests' && top[0]) { return testPrompt(top[0]); }
  if (id === 'vulnerabilities' && top.length === 1) { return vulnerabilityPrompt(top[0]); }
  if (id === 'vulnerabilities') { return `${top.map(vulnerabilityPrompt).join('\n\n---\n\n')}`; }
  return findingsPrompt(id, top);
}

/** A failing gate rule's prompt (the release gate card). */
export function gateRulePrompt(rule, card = null) {
  const top = card?.top || [];
  if (top.length) { return `${cardPrompt(rule.check, card)}\n\nThis blocks Publish: "${rule.detail}".`; }
  return `This blocks publishing the project: "${rule.detail}" (Quality check "${checkTitle(rule.check)}"). Fix it, then run the check again.`;
}

/** The prompt to write tests for the least covered files. */
export function coveragePrompt(files = [], min = 70) {
  const lines = [`Write tests for the least covered files of this project, so that line coverage reaches at least ${min}%. Then run the tests with coverage again.`, ''];
  files.slice(0, 5).forEach((f) => lines.push(`- ${f.path}: lines ${pct(f.lines)}${f.uncovered ? `, not covered: ${f.uncovered}` : ''}`));
  return lines.join('\n');
}

export const NO_TESTS_PROMPT = 'This project has no tests yet. Add a test setup to each service (Vitest for Node, pytest for Python) with tests for the main behaviour, and a coverage report, then run them.';

/** "Write tests with AI": a test setup and a first test per service, with coverage, so the Tests check measures them. */
export function writeTestsPrompt(services = []) {
  const names = services.filter(Boolean);
  const lines = [
    `This project has no tests yet${names.length ? ` (services: ${names.join(', ')})` : ''}. For each service:`,
    '- add a test setup: Vitest for Node and front-end services (Jest if the service already uses it), pytest with pytest-cov for Python;',
    '- add a `test` script (Node: `vitest run --coverage`, with @vitest/coverage-v8 as a dev dependency) so the tests run with coverage;',
    '- write a first test of the main behaviour (an endpoint, a page or a function that matters), and make it pass.',
    'Keep the app\'s behaviour the same. Then tell me which tests you added; I will run them in the Quality tab.',
  ];
  return lines.join('\n');
}

/** A production-readiness item (or all of them): what is wrong, why it matters, where, how to fix it. */
export function readinessPrompt(items = []) {
  const lines = [`This project has ${plural(items.length, 'thing')} to fix before it works in production (outside Flow's preview). Fix them and tell me what you changed:`, ''];
  items.slice(0, 20).forEach((item) => {
    const where = item.file ? `${item.file}${item.line ? `:${item.line}` : ''}` : (item.service ? `services/${item.service}` : '');
    lines.push(`- ${item.title || item.message}${where ? ` (${where})` : ''}`);
    if (item.why) { lines.push(`  Why: ${item.why}`); }
    if (item.fix) { lines.push(`  Fix: ${item.fix}`); }
  });
  return lines.join('\n');
}

/** A readiness finding as an item (title, why, where, fix). */
export const readinessItemOf = (f) => ({
  id: f.id, rule: f.rule, title: f.extra?.title || f.message, why: f.extra?.why || null, fix: f.fix || null, file: f.file || null, line: f.line || null, service: f.service || null, blocks: Boolean(f.extra?.blocks),
});

/** Accessibility issues: the rule, the element, the help; fix them in the page's code. */
export function accessibilityPrompt(findings = []) {
  const lines = [`The accessibility check (axe-core, WCAG 2.2 AA) found ${plural(findings.length, 'problem')} in the running app. Fix them in the pages' code, then I will run the check again:`, ''];
  findings.slice(0, 20).forEach((f) => {
    const x = f.extra || {};
    lines.push(`- [${x.impact || severityOf(f.severity).label}] ${f.rule}: ${f.message}${f.service ? ` (${f.service})` : ''}${x.selector ? `, element ${x.selector}` : ''}${x.helpUrl ? `. Help: ${x.helpUrl}` : ''}`);
  });
  return lines.join('\n');
}

// ---- settings -------------------------------------------------------------------------------------------------------

/** The when-table: the v1 checks with their typical time, then the ones coming soon. */
export const WHEN_ROWS = [
  { id: 'tests', name: 'Tests', time: '1-3 min' },
  { id: 'readiness', name: 'Ready for production', time: '5 s' },
  { id: 'lint', name: 'Lint and formatting', time: '10 s' },
  { id: 'codeStats', name: 'Code stats', time: '10 s' },
  { id: 'secrets', name: 'Secrets in code', time: '5 s' },
  { id: 'security', name: 'Security (code, config, containers)', time: '20 s' },
  { id: 'vulnerabilities', name: 'Vulnerabilities (CVE)', time: '30 s' },
  { id: 'dependencies', name: 'Dependencies and licences', time: '30 s' },
  { id: 'sbom', name: 'SBOM', time: '10 s' },
  { id: 'privacy', name: 'Privacy', time: '20 s' },
  { id: 'apiContract', name: 'API contract', time: '10 s' },
  { id: 'accessibility', name: 'Accessibility (in your browser)', time: '30 s per page', browser: true },
  { id: 'performance', name: 'Performance budgets', time: '1-3 min' },
  { id: 'codeScanning', name: 'Code scanning (Semgrep, Hadolint)', time: '1-2 min' },
  { id: 'external', name: 'External tools (SonarQube, Semgrep AppSec Platform)', time: '1-5 min' },
  { id: 'lighthouse', name: 'Lighthouse', time: '30 s per page' },
];

/** The gate rules the settings show, in order; the last ones are coming soon. */
export const GATE_ROWS = [
  { id: 'criticalVulnerabilities', text: 'Any critical vulnerability' },
  { id: 'highVulnerabilitiesWithFix', text: 'A high vulnerability with a fix, open more than', number: { key: 'days', min: 0, max: 365, unit: 'days', label: 'Days' } },
  { id: 'failingTests', text: 'Any failing test (flaky tests in quarantine do not count)' },
  { id: 'coverage', text: 'Line coverage below', number: { key: 'min', min: 0, max: 100, unit: '% in any service', label: 'Percent' } },
  { id: 'secrets', text: 'A live secret in the code (test keys only warn)' },
  { id: 'lintErrors', text: 'Lint errors (warnings never block)' },
  { id: 'licences', text: 'A licence not on the allow list' },
  { id: 'readiness', text: 'Something that breaks the app in production', flag: { key: 'dockerfile', label: 'Also a service without a Dockerfile' } },
  { id: 'accessibility', text: 'More than', number: { key: 'max', min: 0, max: 1000, unit: 'serious accessibility issues', label: 'Issues' } },
  { id: 'bundleBudget', text: 'A web bundle over', number: { key: 'kb', min: 10, max: 100000, unit: 'KB (gzip)', label: 'Kilobytes' } },
  { id: 'p95Budget', text: 'A server route slower than', number: { key: 'ms', min: 10, max: 600000, unit: 'ms (p95, from Traces)', label: 'Milliseconds' } },
  { id: 'privacyLogs', text: 'Personal data written to logs' },
  { id: 'breakingApi', text: 'A breaking API change since the last publish' },
  { id: 'provenance', text: 'A release without signed provenance' },
  { id: 'externalGate', text: 'An external quality gate fails' },
  { id: 'codeScanning', text: 'High or critical code scanning findings' },
  { id: 'lighthouse', text: 'A Lighthouse score under', number: { key: 'min', min: 0, max: 100, unit: 'in any category', label: 'Score' } },
];

/** The code stats thresholds (Settings): empty means none; over one, the card asks for attention (it never blocks). */
export const CODE_STATS_ROWS = [
  { key: 'maxComplexity', text: 'A function more complex than', unit: '(cyclomatic complexity)', min: 1, max: 1000 },
  { key: 'maxFileLines', text: 'A file longer than', unit: 'lines', min: 10, max: 1000000 },
  { key: 'maxDuplication', text: 'More than', unit: '% of the code duplicated', min: 0, max: 100 },
];

/** A gate rule's on/off, whether it is `true` or `{ on, ... }`. */
export const ruleOn = (value) => (value && typeof value === 'object' ? Boolean(value.on) : Boolean(value));

/** The settings with one gate rule switched. */
export function withRuleOn(settings, id, on) {
  const current = settings.gate?.[id];
  const next = current && typeof current === 'object' ? { ...current, on } : on;
  return { ...settings, gate: { ...settings.gate, [id]: next } };
}

export function withRuleNumber(settings, id, key, value) {
  const current = settings.gate?.[id];
  const base = current && typeof current === 'object' ? current : { on: Boolean(current) };
  return { ...settings, gate: { ...settings.gate, [id]: { ...base, [key]: value } } };
}

/** The settings with one flag of an object gate rule set (`readiness.dockerfile`). */
export function withRuleFlag(settings, id, key, value) {
  const current = settings.gate?.[id];
  const base = current && typeof current === 'object' ? current : { on: Boolean(current) };
  return { ...settings, gate: { ...settings.gate, [id]: { ...base, [key]: value } } };
}

export function withWhen(settings, id, key, value) {
  return { ...settings, when: { ...settings.when, [id]: { ...(settings.when?.[id] || {}), [key]: value } } };
}

/** The checks an ignore rule may name (the SBOM has no findings). */
export const IGNORE_CHECKS = CHECK_IDS.filter((id) => id !== 'sbom');

/** YYYY-MM-DD `days` after `now`. */
export function dateIn(days, now = Date.now()) {
  return new Date(now + days * 86400000).toISOString().slice(0, 10);
}

/** Whole days from now to a YYYY-MM-DD date (negative once past). */
export function daysUntil(date, now = Date.now()) {
  if (!date) { return null; }
  const end = Date.parse(`${date}T23:59:59Z`);
  if (Number.isNaN(end)) { return null; }
  return Math.floor((end - now) / 86400000);
}

/** "31 Dec 2026" */
export function formatDate(date) {
  if (!date) { return ''; }
  const d = new Date(`${date}T00:00:00Z`);
  return Number.isNaN(d.getTime()) ? String(date) : d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', timeZone: 'UTC' });
}

/**
 * The ignore form's errors (`{ what?, reason?, until? }`): what is required, a reason of 3..500 characters, an end date
 * (YYYY-MM-DD, after today, at most a year ahead) unless the rule is for secrets.
 */
export function validateIgnore(form, now = Date.now()) {
  const errors = {};
  if (!String(form.what || '').trim()) { errors.what = 'Say what to ignore: a rule, an advisory id, a package or a finding id.'; }
  const reason = String(form.reason || '').trim();
  if (reason.length < 3) { errors.reason = 'A reason is required (at least 3 characters).'; } else if (reason.length > 500) { errors.reason = 'At most 500 characters.'; }
  if (!form.until) {
    if (form.check !== 'secrets') { errors.until = 'An end date is required (only secrets rules may have none).'; }
  } else {
    const left = daysUntil(form.until, now);
    if (left === null || !/^\d{4}-\d{2}-\d{2}$/.test(form.until)) { errors.until = 'Use a date like 2026-12-31.'; } else if (left < 0) { errors.until = 'The date is in the past.'; } else if (left > 366) { errors.until = 'At most one year ahead.'; }
  }
  return errors;
}

/** The validation errors of a 400 (`body.errors: [{ path, message }]`) by path. */
export function errorsByPath(error) {
  const list = error?.body?.errors || [];
  return Object.fromEntries(list.filter((e) => e?.path).map((e) => [e.path, e.message]));
}

/** An error's words. */
export const errorText = (error) => error?.body?.error || error?.message || String(error || 'Something went wrong');

/**
 * Why Run (Run all checks, Run it, Retry, Start and test) did not start the checks, in plain words, shown next to the
 * button that was pressed. A 502/504 or a network failure is the sandbox restarting (2026-11-02: a lint that blocked
 * vfs-service got the container killed by its liveness probe; the click only got a 502), a 503 one starting up.
 */
export function runErrorText(error) {
  const status = error?.status;
  if (error?.code === 'busy') { return 'A run is already going on: its progress is at the top of Quality.'; }
  if (error?.code === 'nothing_to_run') { return 'Nothing to run: no check applies to this project yet.'; }
  if (error?.code === 'sandbox_busy') { return 'Your sandbox is already running checks for other projects. Try again in a minute.'; }
  if (status === 502 || status === 504 || (!status && (error?.name === 'TypeError' || /failed to fetch|network/i.test(error?.message || '')))) {
    return 'Your sandbox restarted while starting the checks, so they did not start. Try again in a moment.';
  }
  if (status === 503) { return 'Your sandbox is starting up or restarting, so the checks did not start. Try again in a moment.'; }
  return `The checks did not start: ${errorText(error)}`;
}

/**
 * The last run the sandbox restarting cut short (the overview's `interrupted`): whether a check is one of its checks
 * not run again since. `checks: []` (an older sandbox's record): which ones is unknown, no card is marked.
 */
export const interruptedCheck = (overview, id) => Boolean(!overview?.running && overview?.interrupted?.checks?.includes(id));

/** Errors that mean "Quality is not there": an older sandbox, no vfs-service. */
export const isUnavailable = (error) => Boolean(error && (error.code === 'sandbox_outdated' || error.status === 0));

// ---- Tests and coverage drill-down (v3) -----------------------------------------------------------------------------

/**
 * The status a card or a page shows: a check whose gate rule fails never says "Passing" next to "Blocks Publish" (an
 * older sandbox could still answer `pass` with `blocks: true`): it says "Attention" instead. A missing lockfile that
 * blocks Publish (a gap of kind `vulnerabilities`, not waived) reads as failing on Vulnerabilities and Licences, never
 * "Info".
 */
export function shownStatus(card) {
  const status = card?.status;
  if ((card?.gaps || []).some((gap) => gap.kind === 'vulnerabilities' && gap.blocking) && ['pass', 'info', 'warn', 'skipped'].includes(status)) { return 'fail'; }
  if (card?.blocks && ['pass', 'info', 'skipped'].includes(status)) { return 'warn'; }
  return status;
}

export const TEST_STATUS = {
  failed: { label: 'Failed', tone: 'fail' },
  flaky: { label: 'Flaky', tone: 'warn' },
  passed: { label: 'Passed', tone: 'pass' },
  skipped: { label: 'Skipped', tone: 'info' },
};
export const testStatusOf = (status) => TEST_STATUS[status] || { label: status || 'Unknown', tone: 'info' };

/** A service-relative file as the Code tab's path: `services/<svc>/<file>` (kept when it already is one). */
export function codePath(service, file) {
  if (!file) { return ''; }
  const clean = String(file).replace(/^\.\//, '');
  if (clean.startsWith('services/') || !service) { return clean; }
  return `services/${service}/${clean}`;
}

/** "Fix with AI" for one named test of a run (TestCase): where, the error, the diff, the stack, its output. */
export function testCasePrompt(test) {
  const f = test.failure || {};
  const lines = [`A test of this project fails. Find out why and fix it, then run the tests of the ${test.service} service again to check.`, ''];
  lines.push(`Test: ${[test.suite, test.name].filter(Boolean).join(' › ')}`);
  if (test.file) { lines.push(`File: ${codePath(test.service, test.file)}${test.line ? `:${test.line}` : ''}`); }
  if (f.message) { lines.push(`Error: ${f.message}`); }
  if (f.diff) { lines.push('', 'Difference:', String(f.diff).split('\n').slice(0, 30).join('\n')); } else {
    if (f.expected !== undefined) { lines.push(`Expected: ${f.expected}`); }
    if (f.received !== undefined) { lines.push(`Received: ${f.received}`); }
  }
  if (f.stack) { lines.push('', 'Stack:', String(f.stack).split('\n').slice(0, 12).join('\n')); }
  if (test.stderr) { lines.push('', 'Its stderr:', String(test.stderr).split('\n').slice(0, 20).join('\n')); }
  lines.push('', 'Fix the code if the test is right; change the test only if it checks the wrong thing, and say which.');
  return lines.join('\n');
}

const two = (n) => String(n).padStart(2, '0');
const RUN_TRIGGERS = { manual: 'manual', publish: 'before Publish', change: 'on change' };

/**
 * A run's label in local time: "19:48:12 · manual", with the date when it is not today ("2026-10-16 19:48:12 ·
 * manual"). From `finishedAt` (else `startedAt`) and `trigger`; else from the server's UTC `label`. Distinct per run,
 * never a truncated id (the full id goes in a title).
 */
export function runLabel(run, now = Date.now()) {
  let ms = run?.finishedAt || run?.startedAt || null;
  let trigger = run?.trigger || null;
  if (!ms && run?.label) {
    const match = /^(\d{4}-\d{2}-\d{2}) (\d{2}:\d{2}:\d{2})(?: · (.+))?$/.exec(run.label);
    if (match) { ms = Date.parse(`${match[1]}T${match[2]}Z`); trigger = trigger || match[3] || null; } else { return run.label; }
  }
  if (!ms) { return run?.runId || 'Run'; }
  const d = new Date(ms);
  const today = new Date(now);
  const time = `${two(d.getHours())}:${two(d.getMinutes())}:${two(d.getSeconds())}`;
  const sameDay = d.getFullYear() === today.getFullYear() && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  const when = sameDay ? time : `${d.getFullYear()}-${two(d.getMonth() + 1)}-${two(d.getDate())} ${time}`;
  return trigger ? `${when} · ${RUN_TRIGGERS[trigger] || trigger}` : when;
}

/** The polyline of values 0-100 over a width (nulls skipped): the coverage trend. */
export function trendPoints(values = [], { width = 240, height = 48, pad = 4 } = {}) {
  const n = values.length;
  if (n < 2) { return ''; }
  const step = (width - 2 * pad) / (n - 1);
  return values
    .map((v, i) => (typeof v === 'number' ? `${(pad + i * step).toFixed(1)},${(height - pad - (Math.max(0, Math.min(100, v)) / 100) * (height - 2 * pad)).toFixed(1)}` : null))
    .filter(Boolean)
    .join(' ');
}

/** "Write tests for this file with AI": the file, its coverage, the lines no test runs. */
export function coverageFilePrompt(file) {
  const m = file.metrics || {};
  const lines = [`Write tests for ${file.path} so that the lines no test runs today are covered. Then run the tests with coverage again.`, ''];
  lines.push(`Coverage now: lines ${pct(m.lines)}, branches ${pct(m.branches)}, functions ${pct(m.functions)}.`);
  if (file.uncoveredRanges) { lines.push(`Lines not covered: ${file.uncoveredRanges}`); }
  lines.push('', 'Test the behaviour, not the implementation details; keep the existing tests passing.');
  return lines.join('\n');
}

/** "Ask AI" instead of Add coverage: the coverage tool per service, then run the tests with coverage. */
export function coverageSetupPrompt(services = []) {
  return [
    `The tests of ${services.length ? services.join(', ') : 'this project'} run, but measure no coverage. Add coverage to each:`,
    '- Vitest: add @vitest/coverage-v8 (same major version as vitest) as a dev dependency and run `vitest run --coverage`;',
    '- Jest: run it with `--coverage`;',
    '- pytest: add pytest-cov to the dev requirements and run `pytest --cov`.',
    'Keep the lockfile in sync, then run the tests with coverage once to check that a report is written.',
  ].join('\n');
}

/**
 * Missing checks (vfs-service gaps.js): no tests, tests Flow can't run here, no coverage report, no linter, no
 * lockfile. They never count as passing: the gate lists them (`gate.gaps`, `gate.waived`), and a rule they fail
 * carries `missing: true` and the gap's label (`gap`). All but no linter block Publish while their rule is on.
 */
export const gapsOf = (gate) => (Array.isArray(gate?.gaps) ? gate.gaps : []);
export const waivedOf = (gate) => (Array.isArray(gate?.waived) ? gate.waived : []);

/** "No tests (api, web) · Tests can't run (engine)": the gaps grouped by label, with their services. */
export function gapsText(gaps = []) {
  const groups = new Map();
  gaps.forEach((gap) => {
    const list = groups.get(gap.label) || [];
    if (gap.service) { list.push(gap.service); }
    groups.set(gap.label, list);
  });
  return [...groups.entries()].map(([label, services]) => (services.length ? `${label} (${services.join(', ')})` : label)).join(' · ');
}

/** A gate rule's state pill: a rule failed by a missing check says "Missing", not "Blocks". */
export function ruleState(rule, blocked) {
  if (rule.status === 'fail' && !blocked) { return { tone: 'warn', label: 'Overridden' }; }
  if (rule.status === 'fail' && rule.missing) { return { tone: 'fail', label: 'Missing' }; }
  return { pass: { tone: 'pass', label: 'Passes' }, fail: { tone: 'fail', label: 'Blocks' }, unknown: { tone: 'info', label: 'Not checked' }, off: { tone: 'info', label: 'Off' } }[rule.status] || { tone: 'info', label: 'Not checked' };
}

/**
 * What a check that found nothing says instead of a bare "Nothing found." when its result has counts (2026-10-31: the
 * product owner read "Nothing found." on Dependencies as "no package found"): "No licence problems: all 197 packages
 * use an allowed licence.", "No secrets in the 412 files scanned.". Null when the result has nothing to count. The
 * ignored findings are said too, so "nothing open" never hides them.
 */
export function passingMessage(checkId, check) {
  const d = check?.details || {};
  let text = null;
  if (checkId === 'dependencies' && d.packages) {
    const licences = d.licences || [];
    const notAllowed = licences.filter((l) => l.allowed === false).reduce((n, l) => n + l.count, 0);
    const unknown = licences.filter((l) => l.allowed !== true && l.allowed !== false).reduce((n, l) => n + l.count, 0);
    text = !licences.length || (!notAllowed && !unknown)
      ? `No licence problems: ${d.packages === 1 ? 'the only package uses' : `all ${d.packages} packages use`} an allowed licence.`
      : `No open findings in ${plural(d.packages, 'package')}.`;
  } else if (checkId === 'vulnerabilities' && d.packages) {
    text = `No known vulnerabilities in the ${plural(d.packages, 'package')} checked.`;
  } else if (['secrets', 'security'].includes(checkId) && d.filesScanned) {
    text = `${checkId === 'secrets' ? 'No secrets' : 'No risky code'} in the ${plural(d.filesScanned, 'file')} scanned.`;
  } else if (checkId === 'lint' && (check.services || []).length) {
    const linted = check.services.filter((s) => s.status === 'pass');
    if (linted.length) { text = `No lint problems in ${linted.map((s) => s.service).join(', ')}.`; }
  }
  if (text && check.ignoredCount) { text = `${text} ${plural(check.ignoredCount, 'finding')} ignored: Show ignored lists ${check.ignoredCount === 1 ? 'it' : 'them'}.`; }
  return text;
}
